// externals
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useState, forwardRef } from 'react';

// styles
import { VehicleInsuranceInfoSlideOutContentWrapper, BodilyInjuryCoverageContainer } from './VehicleInsuranceSlideOut.style';

// libraries
import { formatAsYYYYMMDD, getUtcDate } from '@makemydeal/dr-common-utils';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// components
import { Grid } from '@interstate/components/Grid';
import { SelectInput } from '@interstate/components/SelectInput';
import { DatePicker } from '@interstate/components/DatePicker';
import { Dropdown } from '../Dropdown/Dropdown';
import Autocomplete from '../autoComplete/Autocomplete';
import { TextInput } from '../TextInput/TextInput';
import { PhoneInput } from '../PhoneInput/PhoneInput';
import { getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import { PriceInput } from '@interstate/components/PriceInput';
import { PostalCodeInput } from '../PostalCodeInput/PostalCodeInput';
import { createSelectOnFocusHandler, useSelectOnFocusEnabled } from '../selectOnFocus/selectOnFocus';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// validation
import { useFormValidation } from '@makemydeal/dr-shared-ui-utils';
import {
    vehicleInsuranceValidationConfig,
    contractApiErroredVehicleInsuranceValidationConfig
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleInsuranceValidationConfig';
import type { SearchOption } from '@makemydeal/dr-shared-ui-utils';

// types
import type { VehicleInsurance } from '@makemydeal/dr-platform-types';
import type { AutocompleteOptions } from '@makemydeal/dr-shared-types';
import { VEHICLE_INSURANCE } from '@makemydeal/dr-shared-types';
import { searchAndSortOptions } from '@makemydeal/dr-shared-ui-utils';

// props contract
import type { InsuranceFormProps, InsuranceFormRef, InsuranceProviderOption } from './InsuranceFormProps';
import { INSURANCE_FORM_ANALYTICS } from './InsuranceFormProps';

export type { InsuranceFormRef };

const InsuranceForm = forwardRef<InsuranceFormRef, InsuranceFormProps>((props, ref) => {
    const {
        vehicleInsuranceInfo,
        isAdditionalInsuranceFieldsEnabled,
        isVehicleInsuranceUpdated,
        isLocked,
        buyerState,
        insuranceProviders,
        isContractingContext,
        contractApiErroredVehicleInsurance,
        onContractingErrorsAlert,
        getInsuranceProviders,
        onSetErrorFields,
        onChange,
        onProviderInteraction,
        hostApp,
        logContext
    } = props;

    const [insuranceProvider, setInsuranceProvider] = useState(vehicleInsuranceInfo?.insuranceCompany || '');
    const [insuranceAgentName, setInsuranceAgentName] = useState(vehicleInsuranceInfo?.agentName || '');
    const [insuranceStreetAddress, setInsuranceStreetAddress] = useState(vehicleInsuranceInfo?.agentAddress?.addressLine1 || '');
    const [insuranceState, setInsuranceState] = useState(vehicleInsuranceInfo?.agentAddress?.stateProvince || '');
    const [insuranceZipCode, setInsuranceZipCode] = useState(vehicleInsuranceInfo?.agentAddress?.postalCode || '');
    const [insuranceCity, setInsuranceCity] = useState(vehicleInsuranceInfo?.agentAddress?.city || '');
    const [insurancePolicyNumber, setInsurancePolicyNumber] = useState(vehicleInsuranceInfo?.policyNumber || '');
    const [hasLiabilityCoverage, setHasLiabilityCoverage] = useState(vehicleInsuranceInfo?.coverage?.hasLiabilityCoverage);
    const [bodilyInjuryPerPerson, setBodilyInjuryPerPerson] = useState(vehicleInsuranceInfo?.coverage?.bodilyInjuryPerPerson || '');
    const [bodilyInjuryPerAccident, setBodilyInjuryPerAccident] = useState(
        vehicleInsuranceInfo?.coverage?.bodilyInjuryPerAccident || ''
    );
    const [policyStartDate, setPolicyStartDate] = useState(
        (vehicleInsuranceInfo?.policyStartDate && getUtcDate(new Date(vehicleInsuranceInfo.policyStartDate)).toISOString()) || null
    );
    const [insuranceExpiration, setInsuranceExpiration] = useState(
        (vehicleInsuranceInfo?.policyExpirationDate &&
            getUtcDate(new Date(vehicleInsuranceInfo.policyExpirationDate)).toISOString()) ||
            null
    );
    const [insuranceCollision, setInsuranceCollision] = useState(vehicleInsuranceInfo?.deductibles?.collisionDeductible || 0);
    const [insuranceComprehensive, setInsuranceComprehensive] = useState(
        vehicleInsuranceInfo?.deductibles?.comprehensiveDeductible || 0
    );
    const { verifiedBy } = vehicleInsuranceInfo ?? {};
    const [insurancePhone, setInsurancePhone] = useState(vehicleInsuranceInfo?.agentPhoneNumber || '');
    const [insuranceAgencyName, setInsuranceAgencyName] = useState(vehicleInsuranceInfo?.insuranceAgencyName || '');

    const insuranceValidator = useFormValidation({
        initialConfig: vehicleInsuranceValidationConfig,
        commitConfig: {
            onCommit: (errorFlags) => {
                onSetErrorFields?.(VEHICLE_INSURANCE, errorFlags);
            }
        }
    });
    const [formErrors, setFormErrors] = useState(() => insuranceValidator.getFormErrorState({ channel: 'inline' }));
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [showContractAlert, setShowContractAlert] = useState(false);
    const isAutoSelectEnabled = useSelectOnFocusEnabled(featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap);

    const apiErrorValidator = useFormValidation({
        initialConfig: contractApiErroredVehicleInsuranceValidationConfig,
        flow: 'contract'
    });

    const [apiFormErrors, setApiFormErrors] = useState(() =>
        apiErrorValidator.getFormErrorState({ channel: 'inline', refresh: false })
    );

    const mergedFormErrors = useMemo(() => ({ ...formErrors, ...apiFormErrors }), [formErrors, apiFormErrors]);

    useLayoutEffect(() => {
        insuranceValidator.setFlow(isContractingContext ? 'default+contract' : 'default');
    }, [isContractingContext]);

    useLayoutEffect(() => {
        insuranceValidator.ctx.values = {
            streetAddress: insuranceStreetAddress,
            city: insuranceCity,
            zip: insuranceZipCode,
            policyNumber: insurancePolicyNumber,
            // For validation purposes, treat anything other than explicit `true` as `false`.
            // This prevents empty/undefined from bypassing CO contracting validations.
            liabilityCoverage: hasLiabilityCoverage === true ? 'true' : 'false',
            bodilyInjuryPerPerson:
                bodilyInjuryPerPerson != null && bodilyInjuryPerPerson !== '' ? Number(bodilyInjuryPerPerson) : undefined,
            bodilyInjuryPerAccident:
                bodilyInjuryPerAccident != null && bodilyInjuryPerAccident !== '' ? Number(bodilyInjuryPerAccident) : undefined,
            insuranceAgencyName: insuranceAgencyName || undefined,
            policyStartDate: policyStartDate || undefined,
            policyExpirationDate: insuranceExpiration || undefined
        };
        insuranceValidator.ctx.buyerState = buyerState ?? null;
    }, [
        insuranceStreetAddress,
        insuranceCity,
        insuranceZipCode,
        insurancePolicyNumber,
        hasLiabilityCoverage,
        buyerState,
        bodilyInjuryPerPerson,
        bodilyInjuryPerAccident,
        insuranceAgencyName,
        policyStartDate,
        insuranceExpiration
    ]);

    useEffect(() => {
        if (contractApiErroredVehicleInsurance) {
            if (contractApiErroredVehicleInsurance.hasApiInsuranceCompanyError) {
                apiErrorValidator.validatePath('apiInsuranceCompany', insuranceProvider);
            }
            if (contractApiErroredVehicleInsurance.hasApiAgentPhoneNumberError) {
                apiErrorValidator.validatePath('apiAgentPhoneNumber', insurancePhone);
            }
            if (contractApiErroredVehicleInsurance.hasApiPolicyNumberError) {
                apiErrorValidator.validatePath('apiPolicyNumber', insurancePolicyNumber);
            }
            if (contractApiErroredVehicleInsurance.hasApiPolicyStartDateError) {
                apiErrorValidator.validatePath('apiPolicyStartDate', policyStartDate);
            }
            if (contractApiErroredVehicleInsurance.hasApiPolicyExpirationDateError) {
                apiErrorValidator.validatePath('apiPolicyExpirationDate', insuranceExpiration);
            }
        } else {
            apiErrorValidator.clearErrors();
        }
        setApiFormErrors(apiErrorValidator.getFormErrorState({ channel: 'inline', refresh: false }));
    }, [
        contractApiErroredVehicleInsurance,
        insuranceProvider,
        insurancePhone,
        insurancePolicyNumber,
        policyStartDate,
        insuranceExpiration
    ]);

    useEffect(() => {
        if (!insuranceProviders.length) {
            getInsuranceProviders();
        }
    }, []);

    useEffect(() => {
        // Refresh=true ensures the engine evaluates current ctx.values with the active flow.
        setFormErrors(insuranceValidator.getFormErrorState({ channel: 'inline', refresh: isContractingContext }));
    }, [insuranceValidator, isContractingContext]);

    const [filteredInsuranceProviders, setFilteredInsuranceProviders] = useState<SearchOption[]>([]);
    const [isSearchingProvider, setIsSearchingProvider] = useState(false);

    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, []);

    useEffect(() => {
        setFilteredInsuranceProviders(insuranceProviders);
    }, [insuranceProviders]);

    useEffect(() => {
        setIsSaveDisabled(insuranceValidator.getHasFormErrors());

        // Banner shows only when a currently-invalid field fails a contract-scoped rule (replicates
        // the deprecated useFormValidationWithContractFlow.getHasContractErrors derivation).
        if (!isContractingContext) {
            setShowContractAlert(false);
            return;
        }
        const invalidPaths = Object.entries(insuranceValidator.getAllErrors())
            .filter(([, result]) => Boolean(result) && result.valid === false)
            .map(([path]) => path);
        const hasContractErrors =
            invalidPaths.length > 0 && insuranceValidator.getHasFormErrors({ flow: 'contract', includePaths: invalidPaths });
        const hasApiContractErrors = Object.values(apiFormErrors).some((result: any) => Boolean(result.hasError));
        setShowContractAlert(hasContractErrors || hasApiContractErrors);
    }, [formErrors, apiFormErrors, isContractingContext, insuranceValidator]);

    useEffect(() => {
        onContractingErrorsAlert?.(showContractAlert);
    }, [showContractAlert, onContractingErrorsAlert]);

    // Check if form has unsaved changes
    const hasUnsavedChanges = () => {
        return (
            insuranceProvider !== (vehicleInsuranceInfo?.insuranceCompany || '') ||
            insuranceAgentName !== (vehicleInsuranceInfo?.agentName || '') ||
            insuranceStreetAddress !== (vehicleInsuranceInfo?.agentAddress?.addressLine1 || '') ||
            insuranceState !== (vehicleInsuranceInfo?.agentAddress?.stateProvince || '') ||
            insuranceZipCode !== (vehicleInsuranceInfo?.agentAddress?.postalCode || '') ||
            insuranceCity !== (vehicleInsuranceInfo?.agentAddress?.city || '') ||
            insurancePolicyNumber !== (vehicleInsuranceInfo?.policyNumber || '') ||
            insurancePhone !== (vehicleInsuranceInfo?.agentPhoneNumber || '') ||
            insuranceCollision !== (vehicleInsuranceInfo?.deductibles?.collisionDeductible || 0) ||
            insuranceComprehensive !== (vehicleInsuranceInfo?.deductibles?.comprehensiveDeductible || 0) ||
            hasLiabilityCoverage !== vehicleInsuranceInfo?.coverage?.hasLiabilityCoverage ||
            policyStartDate !==
                ((vehicleInsuranceInfo?.policyStartDate &&
                    getUtcDate(new Date(vehicleInsuranceInfo.policyStartDate)).toISOString()) ||
                    null) ||
            insuranceExpiration !==
                ((vehicleInsuranceInfo?.policyExpirationDate &&
                    getUtcDate(new Date(vehicleInsuranceInfo.policyExpirationDate)).toISOString()) ||
                    null) ||
            Number(bodilyInjuryPerPerson || 0) !== (vehicleInsuranceInfo?.coverage?.bodilyInjuryPerPerson || 0) ||
            Number(bodilyInjuryPerAccident || 0) !== (vehicleInsuranceInfo?.coverage?.bodilyInjuryPerAccident || 0) ||
            insuranceAgencyName !== (vehicleInsuranceInfo?.insuranceAgencyName || '')
        );
    };

    const isDirty = hasUnsavedChanges();

    // Cast once: the external VehicleInsurance type declares the policy dates as Date | null, but the
    // backend contract (and the original form) sends YYYY-MM-DD strings.
    const buildVehicleInsurancePayload = (): VehicleInsurance =>
        ({
            ...vehicleInsuranceInfo,
            insuranceCompany: insuranceProvider,
            policyNumber: insurancePolicyNumber,
            agentName: insuranceAgentName,
            agentAddress: {
                addressLine1: insuranceStreetAddress,
                city: insuranceCity,
                postalCode: insuranceZipCode,
                stateProvince: insuranceState
            },
            agentPhoneNumber: insurancePhone,
            deductibles: {
                comprehensiveDeductible: Number(insuranceComprehensive),
                collisionDeductible: Number(insuranceCollision)
            },
            coverage: {
                hasLiabilityCoverage: hasLiabilityCoverage,
                ...(isAdditionalInsuranceFieldsEnabled && {
                    bodilyInjuryPerPerson:
                        bodilyInjuryPerPerson != null && bodilyInjuryPerPerson !== '' ? Number(bodilyInjuryPerPerson) : undefined,
                    bodilyInjuryPerAccident:
                        bodilyInjuryPerAccident != null && bodilyInjuryPerAccident !== ''
                            ? Number(bodilyInjuryPerAccident)
                            : undefined
                })
            },
            ...(isAdditionalInsuranceFieldsEnabled && {
                insuranceAgencyName: insuranceAgencyName || undefined
            }),
            policyStartDate: policyStartDate && formatAsYYYYMMDD(new Date(policyStartDate)),
            policyExpirationDate: insuranceExpiration && formatAsYYYYMMDD(new Date(insuranceExpiration))
        } as unknown as VehicleInsurance);

    useImperativeHandle(ref, () => ({
        validate: async () => {
            insuranceValidator.commit(['policyNumber', 'liabilityCoverage', 'policyExpirationDate']);
            const errors = insuranceValidator.getFormErrorState({ channel: 'inline', refresh: true });
            setFormErrors(errors);
            return !insuranceValidator.getHasFormErrors();
        },
        getValues: () => buildVehicleInsurancePayload(),
        isDirty: () => hasUnsavedChanges()
    }));

    // Report validity + dirtiness to parent container.
    useEffect(() => {
        onChange?.(isDirty, !isSaveDisabled);
    }, [isDirty, isSaveDisabled, onChange]);

    const onChangeInsuranceField = (e: any, field: string) => {
        const value = e.target.value;
        const trimmedValue = field === 'streetAddress' || field === 'city' ? value.trim() : value;

        if (
            [
                'streetAddress',
                'zipCode',
                'city',
                'policyNumber',
                'bodilyInjuryPerPerson',
                'bodilyInjuryPerAccident',
                'insuranceAgencyName'
            ].includes(field)
        ) {
            if (field === 'zipCode') insuranceValidator.validatePath('zip', trimmedValue);
            else if (field === 'bodilyInjuryPerPerson' || field === 'bodilyInjuryPerAccident') {
                const numVal = trimmedValue != null && trimmedValue !== '' ? Number(trimmedValue) : undefined;
                insuranceValidator.validatePath(field, numVal);
            } else insuranceValidator.validatePath(field, trimmedValue);
        } else if (field !== 'policyStartDate' && field !== 'expiration') {
            // For non-validated fields, still update the engine context so that any dependent validations can react to changes.
            // Autocomplete is setting isValid as undefined for name field, causing engine to treat it as invalid.
            const isValid = e.isValid !== false;
            insuranceValidator.setError(field, isValid, '');
        }
        switch (field) {
            case 'provider':
                setInsuranceProvider(trimmedValue);
                break;
            case 'agentName':
                setInsuranceAgentName(trimmedValue);
                break;
            case 'streetAddress':
                setInsuranceStreetAddress(trimmedValue);
                break;
            case 'state':
                setInsuranceState(trimmedValue);
                break;
            case 'zipCode':
                setInsuranceZipCode(trimmedValue);
                break;
            case 'city':
                setInsuranceCity(trimmedValue);
                break;
            case 'policyNumber':
                setInsurancePolicyNumber(e.target.value);
                break;
            case 'liabilityCoverage': {
                const nextHasLiabilityCoverage =
                    e.target.value === '' || e.target.value == null ? undefined : e.target.value === 'true';

                insuranceValidator.validatePath('liabilityCoverage', nextHasLiabilityCoverage === true ? 'true' : 'false');
                setHasLiabilityCoverage(nextHasLiabilityCoverage);
                break;
            }
            case 'policyStartDate': {
                const policyStartDateValue = e.target.value.dateValue;
                setPolicyStartDate(policyStartDateValue);
                insuranceValidator.ctx.values.policyStartDate = policyStartDateValue;
                insuranceValidator.validatePath('policyExpirationDate', insuranceExpiration);
                break;
            }
            case 'expiration': {
                const expirationValue = e.target.value.dateValue;
                setInsuranceExpiration(expirationValue);
                insuranceValidator.validatePath('policyExpirationDate', expirationValue);
                break;
            }
            case 'collisionDeductible':
                setInsuranceCollision(e.target.value);
                break;
            case 'comprehensiveDeductible':
                setInsuranceComprehensive(e.target.value);
                break;
            case 'phone':
                setInsurancePhone(e.target.value);
                break;
            case 'bodilyInjuryPerPerson': {
                setBodilyInjuryPerPerson(e.target.value);
                break;
            }
            case 'bodilyInjuryPerAccident': {
                setBodilyInjuryPerAccident(e.target.value);
                break;
            }
            case 'insuranceAgencyName': {
                setInsuranceAgencyName(e.target.value);
                break;
            }
        }
        // Update component state with engine state
        const errors = insuranceValidator.getFormErrorState({ channel: 'inline', refresh: false });
        setFormErrors(errors);
    };

    const logNRAction = useCallback(
        (attributes: Record<string, string | boolean>) => {
            const name = hostApp ? `${hostApp}:InsuranceForm` : 'InsuranceForm';
            logNewRelicPageAction(name, { ...logContext, ...attributes });
        },
        [hostApp, logContext]
    );

    const onSearchProvider = (input: string) => {
        setIsSearchingProvider(true);

        // set local provider state
        onChangeInsuranceField({ target: { value: input } }, 'provider');

        // filter provider list when input length is greater than 3
        if (input.length >= 3) {
            const filteredData = searchAndSortOptions(input, insuranceProviders);

            setFilteredInsuranceProviders(filteredData);
            onProviderInteraction?.(INSURANCE_FORM_ANALYTICS.SEARCH_INSURANCE_PROVIDER_NAME);
            logNRAction({ event: 'ProvidersSearch', searchTerm: input, resultsFound: filteredData.length > 0 });
        } else {
            setFilteredInsuranceProviders([]);
        }

        setIsSearchingProvider(false);
    };

    const providerInput = (
        <Grid key="insurance-provider" xs={6}>
            <TextInput
                id="vehicle-insurance-provider"
                label="Provider"
                name="insurance-provider"
                onChange={(e) => onChangeInsuranceField(e, 'provider')}
                value={insuranceProvider}
                data-testid="vehicle-insurance-provider"
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                errorMessage={mergedFormErrors.apiInsuranceCompany.errorMessage}
                hasError={Boolean(mergedFormErrors.apiInsuranceCompany.hasError)}
            />
        </Grid>
    );

    const changeInsuranceProvider = (e: AutocompleteOptions) => {
        const provider = e as InsuranceProviderOption;
        const currentSearchTerm = insuranceProvider;

        // clear filtered list
        setFilteredInsuranceProviders([]);

        // set all the fields
        onChangeInsuranceField({ target: { value: provider.label } }, 'provider');
        onChangeInsuranceField({ target: { value: provider.Address } }, 'streetAddress');
        onChangeInsuranceField({ target: { value: provider.City } }, 'city');
        onChangeInsuranceField({ target: { value: provider.ZIP } }, 'zipCode');
        onChangeInsuranceField({ target: { value: provider.State } }, 'state');
        onChangeInsuranceField({ target: { value: (provider.Phone ?? '').replace(/\D/g, '') } }, 'phone');
        onChangeInsuranceField({ target: { value: provider.Address } }, 'streetAddress');

        onProviderInteraction?.(INSURANCE_FORM_ANALYTICS.AUTOCOMPLETE_INSURANCE_PROVIDER_NAME);
        logNRAction({
            event: 'ProvidersSelect',
            searchTerm: currentSearchTerm,
            ...(provider.label && { selection: provider.label })
        });
    };

    const providerWithAutocomplete = (
        <Grid key="insurance-provider" xs={6}>
            <Autocomplete
                label="Provider"
                menuOptions={filteredInsuranceProviders}
                id="vehicle-insurance-provider"
                isLoading={isSearchingProvider}
                data-testid="vehicle-insurance-provider"
                value={insuranceProvider}
                onChange={(value) => {
                    setInsuranceProvider(value);
                }}
                onSearch={onSearchProvider}
                onSelect={changeInsuranceProvider}
                onFocus={() => {
                    onProviderInteraction?.(INSURANCE_FORM_ANALYTICS.SELECT_INSURANCE_PROVIDER_NAME);
                }}
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                errorMessage={mergedFormErrors.apiInsuranceCompany.errorMessage}
                hasError={Boolean(mergedFormErrors.apiInsuranceCompany.hasError)}
            />
        </Grid>
    );

    const agentName = (
        <Grid key="agent-name" xs={6}>
            <TextInput
                id="vehicle-agent-name"
                label="Agent Name"
                name="agent-name"
                onChange={(e) => onChangeInsuranceField(e, 'agentName')}
                value={insuranceAgentName}
                data-testid="vehicle-agent-name"
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </Grid>
    );

    const streetAddress = (
        <Grid key="street-address" xs={12}>
            <TextInput
                id="vehicle-street-address"
                label="Street Address"
                name="street-address"
                onChange={(e) => onChangeInsuranceField(e, 'streetAddress')}
                value={insuranceStreetAddress}
                data-testid="vehicle-street-address"
                errorMessage={mergedFormErrors.streetAddress.errorMessage}
                hasError={Boolean(mergedFormErrors.streetAddress.hasError)}
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </Grid>
    );

    const stateField = (
        <Grid key="state" xs={3}>
            <Dropdown
                label="State"
                name="state"
                onChange={(e) => onChangeInsuranceField(e, 'state')}
                options={stateSelectInputValues}
                data-testid="vehicle-state"
                value={insuranceState}
                maxHeight="250px"
                placeholder="Select"
                allowRemoval={true}
                disabled={isLocked}
            />
        </Grid>
    );

    const zipCode = (
        <Grid key="zip-code" xs={3}>
            <PostalCodeInput
                id="vehicle-zip-code"
                label="ZIP Code"
                name="zip-code"
                onChange={(e) => onChangeInsuranceField(e, 'zipCode')}
                value={insuranceZipCode}
                data-testid="vehicle-zip-code"
                country="US"
                customErrorMessages={{
                    invalidPostalCode: 'Format must be 12345'
                }}
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </Grid>
    );

    const city = (
        <Grid key="city" xs={6}>
            <TextInput
                id="vehicle-city"
                label="City"
                name="city"
                onChange={(e) => onChangeInsuranceField(e, 'city')}
                value={insuranceCity}
                data-testid="vehicle-city"
                errorMessage={mergedFormErrors.city.errorMessage}
                hasError={Boolean(mergedFormErrors.city.hasError)}
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </Grid>
    );

    const policyNumber = (
        <Grid key="policy-number" xs={6}>
            <TextInput
                id="vehicle-policy-number"
                label="Policy Number"
                name="policy-number"
                onChange={(e) => onChangeInsuranceField(e, 'policyNumber')}
                value={insurancePolicyNumber}
                data-testid="vehicle-policy-number"
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                errorMessage={mergedFormErrors.policyNumber.errorMessage || mergedFormErrors.apiPolicyNumber.errorMessage}
                hasError={Boolean(mergedFormErrors.policyNumber.hasError || mergedFormErrors.apiPolicyNumber.hasError)}
            />
        </Grid>
    );

    const liabilityCoverageOptions = [
        { label: 'Buyer Has Liability Insurance', value: 'true' },
        { label: "Buyer Doesn't Have Liability Insurance", value: 'false' }
    ];

    const liabilityCoverage = (
        <Grid key="liability-coverage" xs={6}>
            <SelectInput
                label="Liability Insurance Statement"
                name="liability-coverage"
                onChange={(e) => onChangeInsuranceField(e, 'liabilityCoverage')}
                options={liabilityCoverageOptions}
                data-testid="vehicle-liability-coverage"
                value={hasLiabilityCoverage === undefined ? '' : String(hasLiabilityCoverage)}
                placeholder="Select Statement"
                disabled={isLocked}
                errorMessage={mergedFormErrors.liabilityCoverage.errorMessage}
                hasError={mergedFormErrors.liabilityCoverage.hasError}
            />
        </Grid>
    );

    const effectiveDateHelpMessage = useMemo(
        () => (policyStartDate && new Date(policyStartDate) > new Date() ? 'Warning: Date is in the future' : undefined),
        [policyStartDate]
    );
    const effectiveDate = (
        <Grid key="policy-start-date" xs={6}>
            <DatePicker
                id="vehicle-policy-start-date"
                label="Effective Date"
                onChange={(e) => onChangeInsuranceField(e, 'policyStartDate')}
                value={policyStartDate ? new Date(policyStartDate) : undefined}
                data-testid="vehicle-policy-start-date"
                name="policy-start-date"
                helpMessage={effectiveDateHelpMessage}
                disabled={isLocked}
                hasError={Boolean(mergedFormErrors.apiPolicyStartDate.hasError)}
                errorMessage={mergedFormErrors.apiPolicyStartDate.errorMessage}
            />
        </Grid>
    );

    const expiration = (
        <Grid key="expiration" xs={6}>
            <DatePicker
                id="vehicle-expiration"
                label="Expiration"
                onChange={(e) => onChangeInsuranceField(e, 'expiration')}
                value={insuranceExpiration ? new Date(insuranceExpiration) : undefined}
                data-testid="vehicle-expiration"
                name="expiration"
                disabled={isLocked}
                hasError={Boolean(
                    mergedFormErrors.policyExpirationDate.hasError || mergedFormErrors.apiPolicyExpirationDate.hasError
                )}
                errorMessage={
                    mergedFormErrors.policyExpirationDate.errorMessage || mergedFormErrors.apiPolicyExpirationDate.errorMessage
                }
            />
        </Grid>
    );

    const collisionDeductible = (
        <Grid key="collision-deductible" xs={6}>
            <PriceInput
                id="vehicle-collision"
                label="Collision Deductible"
                name="collision-deductible"
                onChange={(e) => onChangeInsuranceField(e, 'collisionDeductible')}
                onFocus={createSelectOnFocusHandler(isAutoSelectEnabled)}
                value={String(insuranceCollision)}
                data-testid="vehicle-collision"
                autoInsertCommas
                disabled={isLocked}
            />
        </Grid>
    );

    const comprehensiveDeductible = (
        <Grid key="comprehensive-deductible" xs={6}>
            <PriceInput
                id="vehicle-comprehensive"
                label="Comprehensive Deductible"
                name="comprehensive-deductible"
                onChange={(e) => onChangeInsuranceField(e, 'comprehensiveDeductible')}
                onFocus={createSelectOnFocusHandler(isAutoSelectEnabled)}
                value={String(insuranceComprehensive)}
                data-testid="vehicle-comprehensive"
                autoInsertCommas
                disabled={isLocked}
            />
        </Grid>
    );

    const insuranceAgency = isAdditionalInsuranceFieldsEnabled ? (
        <Grid key="insurance-agency-name" xs={6}>
            <TextInput
                id="vehicle-insurance-agency-name"
                label="Insurance Agency"
                name="insurance-agency-name"
                value={insuranceAgencyName}
                data-testid="vehicle-insurance-agency-name"
                disabled={isLocked}
                hasError={mergedFormErrors.insuranceAgencyName.hasError}
                errorMessage={mergedFormErrors.insuranceAgencyName.errorMessage}
                onChange={(e) => onChangeInsuranceField(e, 'insuranceAgencyName')}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </Grid>
    ) : null;

    const bodilyInjuryCoverage = isAdditionalInsuranceFieldsEnabled ? (
        <Grid key="bodily-injury-coverage" xs={12}>
            <BodilyInjuryCoverageContainer>
                <Typography variant="h5" sx={{ marginBottom: '0.625rem' }}>
                    Bodily Injury Coverage
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={6}>
                        <PriceInput
                            id="vehicle-bodily-injury-per-person"
                            label="Bodily Injury Per Person"
                            name="bodily-injury-per-person"
                            value={String(bodilyInjuryPerPerson)}
                            data-testid="vehicle-bodily-injury-per-person"
                            autoInsertCommas
                            allowDecimal
                            disabled={isLocked}
                            hasError={mergedFormErrors.bodilyInjuryPerPerson.hasError}
                            errorMessage={mergedFormErrors.bodilyInjuryPerPerson.errorMessage}
                            onChange={(e) => onChangeInsuranceField(e, 'bodilyInjuryPerPerson')}
                            onFocus={createSelectOnFocusHandler(isAutoSelectEnabled)}
                        />
                    </Grid>
                    <Grid xs={6}>
                        <PriceInput
                            id="vehicle-bodily-injury-per-accident"
                            label="Bodily Injury Per Accident"
                            name="bodily-injury-per-accident"
                            value={String(bodilyInjuryPerAccident)}
                            data-testid="vehicle-bodily-injury-per-accident"
                            autoInsertCommas
                            allowDecimal
                            disabled={isLocked}
                            hasError={mergedFormErrors.bodilyInjuryPerAccident.hasError}
                            errorMessage={mergedFormErrors.bodilyInjuryPerAccident.errorMessage}
                            onChange={(e) => onChangeInsuranceField(e, 'bodilyInjuryPerAccident')}
                            onFocus={createSelectOnFocusHandler(isAutoSelectEnabled)}
                        />
                    </Grid>
                </Grid>
            </BodilyInjuryCoverageContainer>
        </Grid>
    ) : null;

    const phone = (
        <Grid key="phone" xs={6}>
            <PhoneInput
                id="vehicle-phone"
                label="Phone"
                name="phone"
                onChange={(e) => onChangeInsuranceField(e, 'phone')}
                value={insurancePhone}
                data-testid="vehicle-phone"
                disabled={isLocked}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                hasError={Boolean(mergedFormErrors.apiAgentPhoneNumber.hasError)}
                errorMessage={mergedFormErrors.apiAgentPhoneNumber.errorMessage}
            />
        </Grid>
    );

    return (
        <VehicleInsuranceInfoSlideOutContentWrapper>
            {verifiedBy && (
                <Box data-testid="verified-badge" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Badge variant="success">Verified</Badge>
                </Box>
            )}
            {isVehicleInsuranceUpdated && (
                <Alert type="info" sx={{ marginTop: '8px' }} data-testid="insurance-updates-alert-banner">
                    <span>Insurance details have been updated. </span>
                </Alert>
            )}

            {showContractAlert && !onContractingErrorsAlert && (
                <Alert type="error" sx={{ margin: '1rem 0' }}>
                    Cannot generate contract. Please fix the highlighted insurance details before continuing.
                </Alert>
            )}

            <Grid container spacing={2} paddingBottom={4} paddingTop={1}>
                {insuranceProviders.length ? providerWithAutocomplete : providerInput}
                {agentName}
                {insuranceAgency}
                <Grid xs={12}>
                    <Typography variant="h5">Mailing Address</Typography>
                </Grid>
                {streetAddress}
                {city}
                {stateField}
                {zipCode}
                <Grid xs={12}>
                    <Typography variant="h5">Policy</Typography>
                </Grid>
                {policyNumber}
                {liabilityCoverage}
                {effectiveDate}
                {expiration}
                {collisionDeductible}
                {comprehensiveDeductible}
                {bodilyInjuryCoverage}
                <Grid xs={12}>
                    <Typography variant="h5">Contact</Typography>
                </Grid>
                {phone}
            </Grid>
        </VehicleInsuranceInfoSlideOutContentWrapper>
    );
});

InsuranceForm.displayName = 'InsuranceForm';

export default InsuranceForm;
